import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { subscribeMailingList } from "@/lib/api";
import Reveal from "@/components/Reveal";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeMailingList(email);
      toast.success(res.data.already_subscribed ? "You're already on the list." : "Welcome aboard. You're on the list.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-black border-t border-white/10 text-bone">
      <div className="px-6 md:px-12 lg:px-20 pt-24 pb-12 max-w-7xl mx-auto">
        <Reveal>
          <h2 className="font-display font-bold leading-[0.95] tracking-tighter text-[13vw] lg:text-[9vw] select-none" data-testid="footer-brand-type">
            David <span className="text-brand">Barnes</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-16 md:mt-24">
          <div className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-6">Join the mailing list</p>
            <form onSubmit={handleSubscribe} data-testid="footer-mailing-list-form" className="flex items-center gap-3 border-b border-white/20 focus-within:border-brand transition-colors duration-300 pb-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="footer-mailing-list-email-input"
                className="w-full bg-transparent outline-none text-base text-slate-200 placeholder:text-slate-600"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-mailing-list-submit"
                className="text-brand hover:text-brand-hover transition-colors duration-300 disabled:opacity-50"
                aria-label="Subscribe"
              >
                <ArrowRight size={20} />
              </button>
            </form>
            <p className="text-sm text-slate-500 mt-4">Occasional notes on balance, breath and building a life you love.</p>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-6">Explore</p>
            <div className="flex flex-col gap-3">
              {[["The Method", "/method"], ["Experiences", "/experiences"], ["Balanced Leader", "/balanced-leader"], ["Events", "/events"], ["Journal", "/journal"], ["FAQ", "/faq"], ["Contact", "/contact"]].map(([label, to]) => (
                <Link key={to} to={to} data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm uppercase tracking-widest text-slate-400 hover:text-brand transition-colors duration-300">
                  {label}
                </Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-6">Contact</p>
            <p className="text-slate-300">David Barnes, Master Coach</p>
            <p className="text-slate-400">RX4 Lakes Limited, Cleator Moor, Cumbria, UK</p>
            <a href="mailto:rx4lakes@outlook.com" data-testid="footer-email-link" className="text-brand hover:text-brand-hover transition-colors duration-300 mt-2 inline-block">
              rx4lakes@outlook.com
            </a>
          </div>

          <div className="md:col-span-2">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-6">Follow</p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" data-testid="footer-social-facebook" aria-label="Facebook" className="text-slate-400 hover:text-brand transition-colors duration-300">
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" data-testid="footer-social-instagram" aria-label="Instagram" className="text-slate-400 hover:text-brand transition-colors duration-300">
                <Instagram size={20} />
              </a>
              <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" data-testid="footer-social-linkedin" aria-label="LinkedIn" className="text-slate-400 hover:text-brand transition-colors duration-300">
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-20 pt-8 border-t border-white/5 text-xs text-slate-600 uppercase tracking-widest font-mono">
          <p data-testid="footer-copyright">© 2026 RX4 Lakes Limited. All rights reserved.</p>
          <p>Make Every Day Count</p>
          <a
            href="https://www.rbxconsulting.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit-rbx"
            className="text-slate-500 hover:text-brand transition-colors duration-300"
          >
            Website made by RBX NORTH
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
